import {
    Button,
    Col,
    Form,
    Input,
    Layout,
    Row,
    Space,
    Table,
    TablePaginationConfig,
    Typography
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IRestrictedUsernamesService } from "@shared/interfaces/restricted-usernames-service.interface";
import { HttpErrorResponse } from "@shared/models/error/http-error-response";
import { CreateRestrictedUsernameDto, RestrictedUsernameDto, UpdateRestrictedUsernameDto } from "@shared/types/restricted-usernames/restricted-usernames.dto";
import { ColumnsType } from "antd/es/table";
import { useMutation, UseMutationResult, useQuery } from "react-query";
import AlertModal from "./AlertModal/AlertModal";
import ChangeRestrictedUsernameModal from "./ChangeRestrictedUsernameModal/ChangeRestrictedUsernameModal";
import styles from "./RestrictedUsernames.module.scss";

const ALL = "All";

const defaultRestrictedUsername: Partial<RestrictedUsernameDto> = {
    username: ""
};

const RestrictedUsernames = () => {
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState(ALL);
    const [platform, setPlatform] = useState(ALL);
    const [searchText, setSearchText] = useState("");
    const [currentPageSize, setPageSize] = useState(20);
    const [containerHeight, setContainerHeight] = useState(0);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [selectedUsername, setSelectedUsername] = useState<RestrictedUsernameDto | null>(null);
    const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

    const restricedUsernamesService = appInject<IRestrictedUsernamesService>(DI_TOKENS.restricedUsernamesService);

    const columns = useMemo(
        () =>
            [
                {
                    key: "id",
                    dataIndex: "id",
                    title: "ID",
                    width: 300,
                },
                {
                    key: "username",
                    dataIndex: "username",
                    title: "Username",
                    width: 150,
                },
                {
                    key: "createdAt",
                    dataIndex: "createdAt",
                    title: "Created At",
                    render: (date: Date | null) =>
                        date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
                    width: 200,
                },
                {
                    key: "updatedAt",
                    dataIndex: "updatedAt",
                    title: "Updated At",
                    render: (date: Date | null) =>
                        date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
                    width: 200,
                },
                {
                    key: "action",
                    title: "Actions",
                    render: (_: null, restrictedUsername: RestrictedUsernameDto) => {
                        return (
                            <Space
                                size="middle"
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                }}
                            >
                                <Button
                                    type="primary"
                                    color="danger"
                                    size="small"
                                    style={{
                                        outline: "none",
                                    }}
                                    onClick={() => {
                                        setSelectedUsername(restrictedUsername);
                                        setIsOpenModal(true);
                                    }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    type="primary"
                                    size="small"
                                    style={{
                                        backgroundColor: "#f5222d",
                                        borderColor: "#f5222d",
                                        outline: "none",
                                    }}
                                    onClick={() => {
                                        setIsAlertModalOpen(true);
                                        setSelectedUsername(restrictedUsername);
                                    }}
                                >
                                    Delete
                                </Button>
                            </Space>
                        );
                    },
                    width: 180,
                },
            ] as ColumnsType<RestrictedUsernameDto>,
        [],
    );

    const restrictedUsernamesQuery = useQuery(
        [
            "download-restricted-usernames-list",
            page,
            currentPageSize,
            debouncedSearchText,
            status,
            platform,
        ],
        () =>
            restricedUsernamesService.list({
                page,
                pageSize: currentPageSize,
                query: debouncedSearchText
            }),
        {
            refetchOnMount: true,
        },
    );

    const updateRestrictedUsernameMutation: UseMutationResult<
        RestrictedUsernameDto,
        unknown,
        UpdateRestrictedUsernameDto,
        unknown
    > = useMutation(
        (data: UpdateRestrictedUsernameDto): Promise<RestrictedUsernameDto> => restricedUsernamesService.update(data),
        {
            onSuccess: () => {
                setSelectedUsername(null);
                setIsOpenModal(false);
                restrictedUsernamesQuery.refetch();
            },
        },
    );

    const createRestrictedUsernameMutation: UseMutationResult<
        RestrictedUsernameDto[],
        unknown,
        CreateRestrictedUsernameDto,
        unknown
    > = useMutation(
        (data: CreateRestrictedUsernameDto): Promise<RestrictedUsernameDto[]> =>
            restricedUsernamesService.create(data),
        {
            onSuccess: () => {
                setSelectedUsername(null);
                setIsOpenModal(false);
                restrictedUsernamesQuery.refetch();
            },
        }
    );


    const deleteRestrictedUsernameMutation: UseMutationResult<
        RestrictedUsernameDto,
        unknown,
        string,
        unknown
    > = useMutation((data): Promise<any> => restricedUsernamesService.delete(data), {
        onSuccess: () => {
            setSelectedUsername(null);
            setIsAlertModalOpen(false);
            restrictedUsernamesQuery.refetch();
        },
    });

    const handleCloseDialog = () => {
        setIsOpenModal(false);
        setSelectedUsername(null);
        createRestrictedUsernameMutation.reset();
        updateRestrictedUsernameMutation.reset();
        deleteRestrictedUsernameMutation.reset();
    };

    const handleSaveDialog = (data: RestrictedUsernameDto | CreateRestrictedUsernameDto) => {
        if ("usernames" in data) {
            createRestrictedUsernameMutation.mutate(data);
        } else if (selectedUsername?.id) {
            updateRestrictedUsernameMutation.mutate({ ...data, id: selectedUsername.id });
        }
    };

    const handleProceedDialog = () => {
        if (selectedUsername?.id) {
            deleteRestrictedUsernameMutation.mutate(selectedUsername.id);
        }
    };

    const containerRef = useRef<any>();

    const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
        setPage(current || 1);
        if (pageSize !== currentPageSize) {
            setPageSize(pageSize || 20);
            setPage(1);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            if ("offsetHeight" in containerRef.current) {
                setContainerHeight(containerRef.current.offsetHeight - 140);
            }
        }, 100);
    }, []);

    // Update debounced search text after a delay
    useEffect(() => {
        const handler = setTimeout(() => {
            setPage(1);
            setDebouncedSearchText(searchText);
        }, 300); // Adjust debounce delay as needed

        // Clear the timeout if the effect re-runs (when searchText changes)
        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    const handleSearch = (e: any) => {
        setSearchText(e.target.value);
    };

    const handleCloseAlertDialog = () => {
        setIsAlertModalOpen(false);
    };

    const getErrorMessage = (error: any) => {
        if (error) {
            if (error instanceof HttpErrorResponse) {
                return error.errors.get("message" as any) as unknown as string;
            }

            return `Unable to delete the username. Please try again.`;
        }
    };

    const resetFilters = () => {
        setSearchText("");
        setStatus(ALL);
        setPlatform(ALL);
    };

    console.log({
        create: createRestrictedUsernameMutation.error,
        update: updateRestrictedUsernameMutation.error
    })

    return (
        <Layout className={styles.tableLayout}>
            {isOpenModal ? (
                <ChangeRestrictedUsernameModal
                    open={isOpenModal}
                    restricedUsername={selectedUsername}
                    onClose={handleCloseDialog}
                    onSave={handleSaveDialog}
                    isLoading={updateRestrictedUsernameMutation.isLoading}
                    isError={updateRestrictedUsernameMutation.isError}
                    error={getErrorMessage(selectedUsername ? updateRestrictedUsernameMutation.error : createRestrictedUsernameMutation.error)}
                />
            ) : (
                <></>
            )}

            {isAlertModalOpen ? (
                <AlertModal
                    open={Boolean(isAlertModalOpen)}
                    description={`Are you sure you want to delete the "${selectedUsername?.username}" username?`}
                    onClose={handleCloseAlertDialog}
                    isLoading={deleteRestrictedUsernameMutation.isLoading}
                    error={getErrorMessage(deleteRestrictedUsernameMutation.error)}
                    onProceed={handleProceedDialog}
                />
            ) : (
                <></>
            )}

            <Row
                style={{
                    height: 32,
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                }}
            >
                <Col
                    span={7}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        rowGap: 5,
                        columnGap: 5,
                        height: "100%",
                    }}
                >
                    <Typography.Title style={{ margin: 0 }} level={5}>
                        Restricted Usernames ({restrictedUsernamesQuery.data?.data?.total})
                    </Typography.Title>

                    <Button
                        type="primary"
                        size="middle"
                        onClick={() => {
                            setSelectedUsername(defaultRestrictedUsername as RestrictedUsernameDto);
                            setIsOpenModal(true);
                        }}
                    >
                        Add Username
                    </Button>

                </Col>
                <Col>
                    <Form.Item className="search-input-container">
                        <Input
                            onChange={handleSearch}
                            value={searchText}
                            name="search"
                            style={{
                                minWidth: 200,
                            }}
                            placeholder="Search by Username"
                        />

                        <Button type="primary" onClick={resetFilters}>
                            Reset
                        </Button>
                    </Form.Item>
                </Col>
            </Row>
            <Layout.Content
                ref={containerRef}
                className={styles.content}
                style={{ backgroundColor: "white" }}
            >
                <Table
                    rowKey="key"
                    dataSource={restrictedUsernamesQuery.data?.data?.usernames || []}
                    onChange={onTableChange}
                    columns={columns}
                    loading={restrictedUsernamesQuery.isFetching}
                    pagination={{
                        current: page,
                        total: restrictedUsernamesQuery.data?.data?.total || 0,
                        pageSize: currentPageSize,
                        showSizeChanger: true,
                    }}
                    size="small"
                    scroll={{ y: containerHeight, x: "max-content" }}
                />
            </Layout.Content>
        </Layout>
    );
};

export default RestrictedUsernames;
